<script setup lang="ts">
import type { SelectOption } from '@camelot/components'
import { CtCallout, CtDatePicker, CtSelect } from '@camelot/components'
import { checkIsWebView, toggleCloseButtonVisible } from '@camelot/webview-utils'
import { translate, useTranslations } from '~/composables/translate/translations'
import { useDateTimeStore } from '~/stores/datetime'

const { isWebview = false } = defineProps<{ isWebview?: boolean }>()

const dateTimeStore = useDateTimeStore()

function handleDatePickerFocus() {
  if (isWebview) {
    try {
      // Hide close button when datepicker opens
      // iOS: pass true (hides button by setting it to nil)
      // Android: pass false (normal logic - false = hide)
      const isIOS = checkIsWebView(window, 'webkit')
      const hideValue = isIOS
      console.error('[DatePicker Focus] Platform:', isIOS ? 'iOS' : 'Android', '| Hiding button with value:', hideValue)
      toggleCloseButtonVisible(window, hideValue)
    }
    catch (e) {
      console.error('[DatePicker Focus] Failed to toggle close button:', e)
      // Camelot library bug: doesn't check if toggleCloseButtonVisibility exists
    }
  }
}

function handleDatePickerBlur() {
  if (isWebview) {
    try {
      // Show close button when datepicker closes
      const isIOS = checkIsWebView(window, 'webkit')
      // iOS: pass false (shows button by restoring it)
      // Android: pass true (normal logic - true = show)
      const showValue = !isIOS
      console.error('[DatePicker Blur] Platform:', isIOS ? 'iOS' : 'Android', '| Showing button with value:', showValue)
      toggleCloseButtonVisible(window, showValue)
    }
    catch (e) {
      console.error('[DatePicker Blur] Failed to toggle close button:', e)
      // Camelot library bug: doesn't check if toggleCloseButtonVisibility exists
    }
  }
}

// Initialize with current year dates to avoid showing 2025 before store loads
const currentYear = new Date().getFullYear()
const currentDate = new Date(currentYear, new Date().getMonth(), new Date().getDate())
const dateRange = ref<Date[]>([currentDate, new Date(currentYear, new Date().getMonth(), new Date().getDate() + 7)])

// Sync from store to component, only when store has BOTH dates AND times
const hasInitialized = ref(false)
watchEffect(() => {
  if (!hasInitialized.value
    && dateTimeStore.startDate.date && dateTimeStore.endDate.date
    && dateTimeStore.startDate.time && dateTimeStore.endDate.time) {
    hasInitialized.value = true
    dateRange.value = [dateTimeStore.startDate.date, dateTimeStore.endDate.date]
  }
})

// When user changes dates, update store and preserve time
watch(dateRange, (value) => {
  if (!hasInitialized.value) {
    return
  } // Don't sync during initial setup

  if (value.length >= 1) {
    const existingTime = dateTimeStore.startDate.time
    if (existingTime) {
      // Use dateTime setter to preserve the time
      const newDateTime = new Date(value[0])
      newDateTime.setHours(existingTime.hours, existingTime.minutes, 0, 0)
      dateTimeStore.startDate.dateTime = newDateTime
    }
    else {
      dateTimeStore.startDate.date = value[0]
    }
  }
  if (value.length >= 2) {
    const existingTime = dateTimeStore.endDate.time
    if (existingTime) {
      // Use dateTime setter to preserve the time
      const newDateTime = new Date(value[1])
      newDateTime.setHours(existingTime.hours, existingTime.minutes, 0, 0)
      dateTimeStore.endDate.dateTime = newDateTime
    }
    else {
      dateTimeStore.endDate.date = value[1]
    }
  }
})

// Generate time options in the format HH:MM
const hourSelection = computed(() => {
  const options: SelectOption[] = []
  for (let hours = 0; hours < 24; hours++) {
    const hourString = String(hours).padStart(2, '0')
    options.push({
      label: `${hourString}:00`,
      value: `${hourString}:00`,
    })
    options.push({
      label: `${hourString}:30`,
      value: `${hourString}:30`,
    })
  }
  return options
})

// Convert between string time format and object time format for start time
const selectedStartTime = computed({
  get: () => {
    if (!dateTimeStore.startDate.time) {
      return undefined
    }
    const {
      hours,
      minutes,
    } = dateTimeStore.startDate.time
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  },
  set: (timeString: string | undefined) => {
    if (!timeString) {
      dateTimeStore.startDate.time = undefined
      return
    }
    const [hours, minutes] = timeString.split(':').map(Number)
    dateTimeStore.startDate.time = {
      hours,
      minutes,
    }
  },
})

// Convert between string time format and object time format for end time
const selectedEndTime = computed({
  get: () => {
    if (!dateTimeStore.endDate.time) {
      return undefined
    }
    const {
      hours,
      minutes,
    } = dateTimeStore.endDate.time
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  },
  set: (timeString: string | undefined) => {
    if (!timeString) {
      dateTimeStore.endDate.time = undefined
      return
    }
    const [hours, minutes] = timeString.split(':').map(Number)
    dateTimeStore.endDate.time = {
      hours,
      minutes,
    }
  },
})

const { dateLocale } = useTranslations()
</script>

<template>
  <article>
    <h3 v-if="!isWebview" class="text-black-100">
      {{ translate('headline_date_time') }}
    </h3>
    <h4 v-if="isWebview" class="text-black-100 mt-32">
      {{ translate('headline_date_time') }}
    </h4>

    <form class="flex flex-col gap-[10px] mt-[22px] lg:flex-row">
      <div class="basis-1/2 flex flex-col gap-8">
        <label class="text-xs font-bold text-black-60">
          {{ translate('field_label_start_date') }}
        </label>
        <div class="flex flex-row gap-4">
          <CtDatePicker
            v-model="dateRange"
            :date-range="{ displayedDate: 'start' }"
            day-selection-mode="sideBySide"
            :format="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
            :class="{ '--error': dateTimeStore.errorStartingDate || dateTimeStore.errorBookingPeriod }"
            class="shrink-0 basis-4/6 md:basis-7/12 lg:max-xl:basis-6/12"
            :min-date="dateTimeStore.minStartingDate"
            :max-date="dateTimeStore.maxStartingDate"
            :locale="dateLocale"
            :year-selection="false"

            prefix-icon="calendar"
            :mobile-button-validate-label="translate('cta_select_button')"
            :mobile-button-close-label="translate('cta_overlay_close')"
            @focus="handleDatePickerFocus"
            @blur="handleDatePickerBlur"
          />
          <CtSelect
            v-model="selectedStartTime"
            :options="hourSelection"
            :class="{ '--error': dateTimeStore.errorStartingDate || dateTimeStore.errorBookingPeriod }"
            placeholder="Select time"
          />
        </div>
        <CtCallout v-if="dateTimeStore.errorStartingDate" variant="error">
          {{ dateTimeStore.errorStartingDate }}
        </CtCallout>
      </div>

      <div class="basis-1/2 flex flex-col gap-8">
        <label class="text-xs font-bold text-black-60">
          {{ translate('field_label_end_date') }}
        </label>
        <div class="flex flex-row gap-4">
          <CtDatePicker
            v-model="dateRange"
            :date-range="{ displayedDate: 'end' }"
            day-selection-mode="sideBySide"
            :format="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
            :class="{ '--error': dateTimeStore.errorEndingDate || dateTimeStore.errorBookingPeriod }"
            class="shrink-0 basis-4/6 md:basis-7/12 lg:max-xl:basis-6/12"
            :min-date="dateTimeStore.minStartingDate"
            :max-date="dateTimeStore.maxStartingDate"
            :locale="dateLocale"
            :year-selection="false"
            prefix-icon="calendar"
            :mobile-button-validate-label="translate('cta_select_button')"
            :mobile-button-close-label="translate('cta_overlay_close')"
            @focus="handleDatePickerFocus"
            @blur="handleDatePickerBlur"
          />
          <CtSelect
            v-model="selectedEndTime"
            :options="hourSelection"
            :class="{ '--error': dateTimeStore.errorEndingDate || dateTimeStore.errorBookingPeriod }"
            placeholder="Select time"
          />
        </div>
        <CtCallout v-if="dateTimeStore.errorEndingDate" variant="error">
          {{ dateTimeStore.errorEndingDate }}
        </CtCallout>
      </div>
    </form>

    <CtCallout v-if="dateTimeStore.errorBookingPeriod" variant="error">
      {{ dateTimeStore.errorBookingPeriod }}
    </CtCallout>
  </article>
</template>
